import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, Check, X } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichText } from "@/components/glossary/Glossary";

export const KIND_LABEL = { TERM: "Terim", GUIDE: "Rehber" };
const err = (x) => toast.error(formatApiError(x.response?.data?.detail));
const EMPTY = { kind: "TERM", title: "", category_id: null, summary: "", body: "", published: false };

export function EntryDialog({ open, entry, cats, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [preview, setPreview] = useState(false);
  useEffect(() => { if (open) { setF(entry ? { ...EMPTY, ...entry } : EMPTY); setPreview(false); } }, [open, entry]);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const submit = async (e) => {
    e.preventDefault();
    const body = { kind: f.kind, title: f.title, category_id: f.category_id || null, summary: f.summary, body: f.body, published: f.published };
    try { entry ? await api.put(`/admin/glossary/entries/${entry.id}`, body) : await api.post("/admin/glossary/entries", body); toast.success("Kaydedildi"); onSaved(); onClose(); }
    catch (x) { err(x); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="glossary-entry-dialog">
        <DialogHeader><DialogTitle>{entry ? "İçeriği düzenle" : "Yeni içerik"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tür</Label>
              <Select value={f.kind} onValueChange={(v) => set({ kind: v, category_id: null })}>
                <SelectTrigger data-testid="glossary-entry-kind"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="TERM">Terim (Sözlük)</SelectItem><SelectItem value="GUIDE">Rehber</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Kategori</Label>
              <Select value={f.category_id || "none"} onValueChange={(v) => set({ category_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="glossary-entry-category"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Kategorisiz</SelectItem>{cats.filter((c) => c.kind === f.kind).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Başlık *</Label><Input data-testid="glossary-entry-title" required minLength={2} maxLength={120} value={f.title} onChange={(e) => set({ title: e.target.value })} /></div>
          <div><Label>Kısa açıklama</Label><Input data-testid="glossary-entry-summary" maxLength={300} value={f.summary} onChange={(e) => set({ summary: e.target.value })} /></div>
          <div>
            <div className="flex items-center justify-between"><Label>İçerik *</Label>
              <button type="button" data-testid="glossary-entry-preview-toggle" className="text-xs text-emerald-800 hover:underline" onClick={() => setPreview(!preview)}>{preview ? "Düzenle" : "Önizle"}</button>
            </div>
            {preview ? <div className="rounded-lg border p-4 min-h-[200px]"><RichText text={f.body} /></div>
              : <Textarea data-testid="glossary-entry-body" required minLength={2} rows={10} value={f.body} onChange={(e) => set({ body: e.target.value })} />}
            <div className="text-[11px] text-slate-400 mt-1">"## " başlık, "- " madde, **kalın** yazı için kullanılır. Paragrafları boş satırla ayırın.</div>
          </div>
          <label className="flex items-center justify-between rounded-lg border p-3"><span className="text-sm font-medium">Yayında</span><Switch data-testid="glossary-entry-published" checked={f.published} onCheckedChange={(v) => set({ published: v })} /></label>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose}>İptal</Button><Button data-testid="glossary-entry-save" type="submit" className="bg-emerald-700 hover:bg-emerald-800">Kaydet</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const CatRow = ({ c, count, onChanged }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(c.name);
  const save = async () => { try { await api.put(`/admin/glossary/categories/${c.id}`, { name, kind: c.kind, order: c.order || 0 }); setEdit(false); onChanged(); } catch (x) { err(x); } };
  const del = async () => { if (!window.confirm("Kategori silinsin mi?")) return; try { await api.delete(`/admin/glossary/categories/${c.id}`); onChanged(); } catch (x) { err(x); } };
  return (
    <div className="flex items-center gap-2 py-2 border-b last:border-0" data-testid={`glossary-cat-row-${c.id}`}>
      {edit ? <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8" data-testid={`glossary-cat-name-${c.id}`} /> : <span className="flex-1 text-sm font-medium">{c.name}</span>}
      <span className="text-xs text-slate-500 w-20">{KIND_LABEL[c.kind]} · {count}</span>
      {edit ? <><Button size="sm" variant="ghost" onClick={save} data-testid={`glossary-cat-save-${c.id}`}><Check className="w-4 h-4" /></Button><Button size="sm" variant="ghost" onClick={() => setEdit(false)}><X className="w-4 h-4" /></Button></>
        : <Button size="sm" variant="ghost" onClick={() => setEdit(true)} data-testid={`glossary-cat-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>}
      <Button size="sm" variant="ghost" onClick={del} data-testid={`glossary-cat-delete-${c.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
    </div>
  );
};

export function CategoriesPanel({ cats, entries, onChanged }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("TERM");
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/glossary/categories", { name, kind, order: cats.length }); setName(""); onChanged(); toast.success("Kategori eklendi"); } catch (x) { err(x); }
  };
  return (
    <Card className="p-5 space-y-4 max-w-2xl" data-testid="glossary-categories-panel">
      <form onSubmit={add} className="flex gap-2 flex-wrap">
        <Input data-testid="glossary-cat-new-name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} placeholder="Yeni kategori adı" className="flex-1 min-w-[180px]" />
        <Select value={kind} onValueChange={setKind}><SelectTrigger className="w-32" data-testid="glossary-cat-new-kind"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="TERM">Terim</SelectItem><SelectItem value="GUIDE">Rehber</SelectItem></SelectContent></Select>
        <Button data-testid="glossary-cat-add" type="submit" className="bg-emerald-700 hover:bg-emerald-800"><Plus className="w-4 h-4 mr-1" />Ekle</Button>
      </form>
      <div>{cats.map((c) => <CatRow key={c.id} c={c} count={entries.filter((e) => e.category_id === c.id).length} onChanged={onChanged} />)}</div>
    </Card>
  );
}

const TopList = ({ title, rows, testid }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="font-semibold text-slate-900 mb-3">{title}</div>
    {rows.length === 0 ? <div className="text-sm text-slate-500">Henüz görüntülenme yok.</div> : rows.map((r, i) => (
      <div key={r.id} className="flex items-center gap-3 py-1.5 text-sm">
        <span className="w-5 text-slate-400 font-mono">{i + 1}</span><span className="flex-1 truncate">{r.title}</span><span className="font-semibold text-emerald-800">{r.views || 0}</span>
      </div>
    ))}
  </Card>
);

export function StatsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/glossary/stats").then(({ data }) => setS(data)).catch(err); }, []);
  if (!s) return <div className="text-slate-500 text-sm">Yükleniyor…</div>;
  return (
    <div className="space-y-5" data-testid="glossary-stats-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Toplam görüntülenme", s.total_views, "total"], ["Terim", s.term_count, "terms"], ["Rehber", s.guide_count, "guides"], ["Yayında", s.published_count, "published"]].map(([l, v, k]) => (
          <Card key={k} className="p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div><div className="text-2xl font-bold text-slate-900" data-testid={`glossary-stat-${k}`}>{v}</div></Card>
        ))}
      </div>
      <Card className="p-5">
        <div className="font-semibold text-slate-900 mb-3">Son 30 gün görüntülenme</div>
        <div className="h-56"><ResponsiveContainer><AreaChart data={s.daily}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="day" tickFormatter={(d) => d.slice(8)} fontSize={11} /><YAxis allowDecimals={false} fontSize={11} width={30} /><Tooltip /><Area dataKey="count" name="Görüntülenme" stroke="#047857" fill="#10b98133" /></AreaChart></ResponsiveContainer></div>
      </Card>
      <div className="grid md:grid-cols-2 gap-4">
        <TopList title="En çok okunan terimler" rows={s.top_terms} testid="glossary-top-terms" />
        <TopList title="En çok okunan rehberler" rows={s.top_guides} testid="glossary-top-guides" />
      </div>
      <div className="text-xs text-slate-400">Aynı ziyaretçinin 24 saat içindeki tekrar açılışları ve bot trafiği sayılmaz.</div>
    </div>
  );
}
